import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogIn, Inbox, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers: { "X-Admin-Token": t } });
      setLeads(res.data); setAuthed(true);
    } catch { toast.error("Invalid admin token."); }
    finally { setLoading(false); }
  };

  const download = async (id, name) => {
    try {
      const res = await axios.get(`${API}/admin/leads/${id}/file`, { headers: { "X-Admin-Token": token }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name || "resume"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("File not available."); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-midnight grid place-items-center px-5" data-testid="admin-login">
        <div className="w-full max-w-sm rounded-2xl bg-white p-8">
          <div className="h-12 w-12 rounded-xl bg-primary-blue/10 grid place-items-center text-primary-blue mb-6"><LogIn /></div>
          <h1 className="font-serifd text-3xl text-midnight">Admin Access</h1>
          <p className="text-slate-500 text-sm mt-1">View consultation leads.</p>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Admin token"
            data-testid="admin-token-input"
            className="mt-6 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:ring-2 focus:ring-teal" />
          <button onClick={() => load(token)} disabled={loading} data-testid="admin-login-btn"
            className="mt-4 w-full rounded-full bg-primary-blue text-white py-3 font-semibold hover:bg-teal transition-colors flex items-center justify-center gap-2">
            {loading ? <Loader2 className="animate-spin" size={18} /> : null} Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white py-16 px-5" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Inbox className="text-primary-blue" />
          <h1 className="font-serifd text-4xl text-midnight">Consultation Leads</h1>
          <span className="ml-auto text-sm text-slate-500">{leads.length} total</span>
        </div>
        <div className="space-y-4">
          {leads.length === 0 && <p className="text-slate-500">No leads yet.</p>}
          {leads.map((l) => (
            <div key={l.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`admin-lead-${l.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-semibold text-midnight text-lg">{l.full_name}</p>
                  <p className="text-sm text-slate-500">{l.email} · {l.phone}</p>
                </div>
                <span className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1 text-sm text-slate-600">
                <p><b>From:</b> {l.current_country || "-"}</p>
                <p><b>Destination:</b> {l.preferred_destination || "-"}</p>
                <p><b>Service:</b> {l.service_required || "-"}</p>
                <p><b>Education:</b> {l.education || "-"}</p>
              </div>
              {l.message && <p className="mt-2 text-sm text-slate-600"><b>Message:</b> {l.message}</p>}
              {l.resume_path && (
                <button onClick={() => download(l.id, l.resume_filename)} data-testid={`admin-download-${l.id}`}
                  className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-teal hover:text-primary-blue transition-colors">
                  <Download size={15} /> {l.resume_filename || "Download resume"}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
